import { useEffect, useState } from 'react';
import { useTranslation } from '@yorukiri/i18n';
import { Avatar, Button, Pill, Select, Switch, TextField } from '@yorukiri/ui';
import { colors } from '@yorukiri/ui/tokens';

type Role = 'editor' | 'viewer';
type Member = { userId: string; name: string; email: string; role: 'owner' | Role };
type PendingInvite = { id: string; email: string; role: Role };

type Props = {
  open: boolean;
  pageId: string;
  pageTitle: string;
  onClose: () => void;
};

const sampleMembers: Member[] = [
  { userId: 'u1', name: 'Alek Singh', email: 'alek@yorukiri.com', role: 'owner' },
  { userId: 'u2', name: 'Sara Kim', email: 'sara@example.com', role: 'editor' },
];

export function ShareDialog({ open, pageId, pageTitle, onClose }: Props) {
  const { t } = useTranslation('share');
  const { t: tCommon } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [members, setMembers] = useState<Member[]>(sampleMembers);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [linkEnabled, setLinkEnabled] = useState(false);
  const [linkRole, setLinkRole] = useState<Role>('viewer');
  const [linkUrl, setLinkUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!linkEnabled) {
      setLinkUrl('');
      return;
    }
    const token = `mock-${pageId.slice(0, 8)}`;
    setLinkUrl(`https://app.yorukiri.com/p/${pageId}?token=${token}`);
  }, [linkEnabled, pageId]);

  if (!open) return null;

  const handleInvite = () => {
    if (!email.trim()) return;
    setPending((p) => [
      ...p,
      { id: `inv-${Date.now()}`, email: email.trim().toLowerCase(), role },
    ]);
    setEmail('');
  };

  const handleCopy = () => {
    if (!linkUrl) return;
    navigator.clipboard.writeText(linkUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,17,20,0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '8vh 24px 24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface,
          borderRadius: 14,
          border: `1px solid ${colors.line2}`,
          boxShadow:
            '0 30px 60px -20px rgba(17,17,20,0.3), 0 10px 20px -10px rgba(17,17,20,0.15)',
          width: '100%',
          maxWidth: 560,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px 14px',
            borderBottom: `1px solid ${colors.line}`,
          }}
        >
          <div
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: colors.accent,
              marginBottom: 4,
            }}
          >
            {t('title')}
          </div>
          <h3
            style={{
              fontWeight: 600,
              letterSpacing: '-0.015em',
              fontSize: 22,
              margin: 0,
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
            }}
          >
            <em style={{ fontStyle: 'italic', color: colors.ink3 }}>{pageTitle}</em>
          </h3>
          <p style={{ fontSize: 12, color: colors.ink3, marginTop: 4 }}>{t('subcopy')}</p>
        </div>

        <div style={{ padding: '16px 22px 0', display: 'flex', gap: 8 }}>
          <TextField
            placeholder={t('invite_placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            wrapperStyle={{ flex: 1 }}
          />
          <RoleSelect value={role} onChange={setRole} />
          <Button onClick={handleInvite} disabled={!email.trim()}>
            {tCommon('invite')}
          </Button>
        </div>

        <div style={{ padding: '18px 22px 6px' }}>
          <SectionLabel>{t('section_people')}</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {members.map((m) => (
              <Row key={m.userId}>
                <Avatar name={m.name} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: colors.ink }}>{m.name}</div>
                  <div style={{ fontSize: 11.5, color: colors.ink3 }}>{m.email}</div>
                </div>
                {m.role === 'owner' ? (
                  <Pill tone="accent">{t('role.owner')}</Pill>
                ) : (
                  <RoleSelect
                    value={m.role}
                    onChange={(r) =>
                      setMembers((prev) => prev.map((x) => (x.userId === m.userId ? { ...x, role: r } : x)))
                    }
                  />
                )}
              </Row>
            ))}
            {pending.map((inv) => (
              <Row key={inv.id}>
                <Avatar name={inv.email} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: colors.ink }}>{inv.email}</div>
                  <div style={{ fontSize: 11.5, color: colors.ink3, fontStyle: 'italic' }}>
                    {t('invite_pending')}
                  </div>
                </div>
                <Pill tone="warn">{t(`role.${inv.role}`)}</Pill>
                <button
                  onClick={() => setPending((p) => p.filter((x) => x.id !== inv.id))}
                  style={closeBtn}
                  aria-label={t('revoke_invite')}
                >
                  ×
                </button>
              </Row>
            ))}
          </div>
        </div>

        <div style={{ padding: '14px 22px 18px', borderTop: `1px solid ${colors.line}`, marginTop: 12 }}>
          <SectionLabel>{t('section_general_access')}</SectionLabel>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 0',
            }}
          >
            <Switch checked={linkEnabled} onChange={(e) => setLinkEnabled(e.target.checked)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: colors.ink, fontWeight: 500 }}>
                {linkEnabled ? t('general_access.link_title') : t('general_access.restricted_title')}
              </div>
              <div style={{ fontSize: 11.5, color: colors.ink3 }}>
                {linkEnabled
                  ? t('general_access.link_description')
                  : t('general_access.restricted_description')}
              </div>
            </div>
            {linkEnabled && <RoleSelect value={linkRole} onChange={setLinkRole} />}
          </div>
          {linkEnabled && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 8,
                padding: '8px 10px',
                background: colors.surface2,
                border: `1px solid ${colors.line2}`,
                borderRadius: 8,
              }}
            >
              <input
                readOnly
                value={linkUrl}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 12,
                  color: colors.ink2,
                  minWidth: 0,
                }}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button variant="secondary" size="sm" onClick={handleCopy}>
                {copied ? tCommon('copied') : tCommon('copy')}
              </Button>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 22px',
            background: colors.surface2,
            borderTop: `1px solid ${colors.line}`,
          }}
        >
          <Button variant="ghost" onClick={onClose}>
            {tCommon('done')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RoleSelect({ value, onChange }: { value: Role; onChange: (r: Role) => void }) {
  const { t } = useTranslation('share');
  return (
    <Select
      size="sm"
      value={value}
      onValueChange={onChange}
      align="end"
      wrapperStyle={{ flexShrink: 0 }}
      triggerStyle={{ minWidth: 110 }}
      options={[
        { value: 'editor', label: t('role.editor'), description: t('role.editor_description') },
        { value: 'viewer', label: t('role.viewer'), description: t('role.viewer_description') },
      ]}
    />
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontVariantNumeric: 'tabular-nums',
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: colors.ink3,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
      }}
    >
      {children}
    </div>
  );
}

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: colors.ink3,
  fontSize: 16,
  cursor: 'pointer',
  padding: '2px 6px',
  fontFamily: 'inherit',
};
